import { TipTapContent, FlexibleTimestamp, ReportSectionDocument } from "./document"
import type { MessageData, Part } from "@/types/chat"
import i18n from "@/i18n"

export interface User {
	id: string
	email: string
	name: string
	role: UserRole
	organizationId: string
	preferences?: UserPreferences
	createdAt: Date
	updatedAt: Date
	isActive: boolean
}

export interface UserPreferences {
	locale: string
	theme: "light" | "dark"
	notifications: NotificationPreferences
}

export interface NotificationPreferences {
	email: boolean
	browser: boolean
	reports: boolean
	system: boolean
}

export interface Organization {
	id: string
	name: string
	code: string
	description?: string
	type: OrganizationType
	address: Address
	contactInfo: ContactInfo
	settings: OrganizationSettings
	logoUrl?: string | null
	brandConfig?: BrandConfig | null
	isSuperAdmin: boolean
	createdAt: Date
	updatedAt: Date
}

export interface Address {
	street: string
	city: string
	postalCode: string
	province: string
	country: string
}

export interface ContactInfo {
	phone?: string
	email?: string
	website?: string
}

export type EvaluatorScoreDisplay = "score" | "label" | "both"

export interface EvaluatorSettings {
	scoreDisplay: EvaluatorScoreDisplay
	showProposedWinner?: boolean
	/** Sobre B visibility matrix — hide pre-publication drafts from non-evaluators (default ON) */
	restrictSubjectiveToEvaluators?: boolean
}

export const DEFAULT_EVALUATOR_SETTINGS: EvaluatorSettings = {
	scoreDisplay: "both",
	showProposedWinner: true,
	restrictSubjectiveToEvaluators: true,
}

export interface OrganizationSettings {
	reportTemplates: ReportTemplate[]
	defaultTemplate?: string
	customFields: CustomField[]
	workflowEnabled: boolean
	reviewRequired: boolean
	evaluator?: EvaluatorSettings
}

export interface Report {
	id: string
	briefDescription: string
	title: string
	content: TipTapContent
	templateId?: string
	authorId: string
	organizationId: string
	status: ReportStatus
	currentResponsible: UserRole // Track who is currently responsible for the report
	reviewStatus?: ReviewStatus
	reviewerId?: string
	reviewComments?: ReviewComment[]
	metadata: ReportMetadata
	createdAt: FlexibleTimestamp
	updatedAt: FlexibleTimestamp
	version: number
	pdfAttachmentUrl?: string // URL of the attached PDF document for published reports
	pdfStoragePath?: string // Permanent storage path for the PDF (not an expiring signed URL)
	extractedTemplateId?: string // ID of the template extracted from this report's PDF
	documentType?: DocumentType
	contractScope?: ContractScope
	expedientId?: string
	hasSections?: boolean
	sectionCount?: number
	sectionMode?: SectionMode
	source?: string // "manual" | "tender_assistant" | "public_imported" | "manual_imported"
}

export interface BudgetLot {
	number: number
	description: string
	amount: number
}

export interface BudgetBreakdown {
	baseAmount: number
	ivaPercentage: number
	ivaAmount: number
	totalAmount: number
	lots?: BudgetLot[]
}

export interface ContractExtensions {
	maxExtensions: number
	extensionPeriod: string
}

export interface ReportMetadata {
	category: ReportCategory
	priority?: Priority
	estimatedBudget?: number
	actualBudget?: number
	description?: string // Initial context description provided by user
	contractScope?: ContractScope
	expedientId?: string
	cpvCodes?: string[]
	budgetBreakdown?: BudgetBreakdown
	locations?: string[]
	contractExtensions?: ContractExtensions
	selectionCriteria?: string
	contractProcessType?: ContractProcessType
	termValue?: number
	termPeriod?: TermPeriod
	relatedReports: string[]
	attachments: Attachment[]
	ijExtractedData?: IJExtractedData
}

export interface ReportTemplate {
	id: string
	name: string
	description: string
	organizationId: string
	category: ReportCategory
	fields: TemplateField[]
	structure: TemplateStructure
	isActive: boolean
	createdAt: Date
	updatedAt: Date
}

export interface TemplateField {
	id: string
	name: string
	type: FieldType
	required: boolean
	placeholder?: string
	validation?: FieldValidation
	defaultValue?: string | number | boolean
	options?: string[] // For select fields
}

export interface TemplateStructure {
	sections: ReportSection[]
	headerConfig: HeaderConfig
	footerConfig: FooterConfig
}

export interface ReportSection {
	id: string
	title: string
	order: number
	required: boolean
	fieldIds: string[]
	description?: string
}

export interface HeaderConfig {
	showOrganizationLogo: boolean
	showDate: boolean
	showBriefDescription: boolean
	customText?: string
}

export interface FooterConfig {
	showPageNumbers: boolean
	showSignature: boolean
	customText?: string
}

export interface CustomField {
	id: string
	name: string
	type: FieldType
	required: boolean
	organizationId: string
}

export interface ChatMessage {
	id: string
	conversationId: string
	type: MessageType
	content: string
	metadata?: MessageMetadata
	timestamp: Date
	userId?: string
}

export interface MessageMetadata {
	extractedData?: ExtractedData
	confidence?: number
	suggestedFields?: SuggestedField[]
}

export interface ExtractedData {
	[key: string]: unknown
}

export interface SuggestedField {
	fieldId: string
	value: string | number | boolean
	confidence: number
}

export interface AIConfiguration {
	primaryProvider: AIProvider
	fallbackProviders: AIProvider[]
	modelSettings: ModelSettings
	promptTemplates: PromptTemplate[]
}

export interface PromptTemplate {
	id: string
	name: string
	template: string
	category: ReportCategory
	variables: string[]
}

export interface ModelSettings {
	temperature: number
	maxTokens: number
	topP: number
	frequencyPenalty: number
	presencePenalty: number
}

/** Legacy inline review comment (embedded in Report.reviewComments) */
export interface ReviewComment {
	id: string
	reviewerId: string
	comment: string
	section?: string
	status: CommentStatus
	createdAt: Date
	resolvedAt?: Date
}

/** Review comment stored in the review_comments DB table */
export interface ReviewCommentRecord {
	id: string
	reportId: string
	/** Null = general comment on the whole report */
	sectionId: string | null
	authorId: string
	content: string
	resolved: boolean
	resolvedAt?: FlexibleTimestamp | null
	resolvedBy?: string | null
	createdAt: FlexibleTimestamp
	updatedAt: FlexibleTimestamp
	/** Populated by joins — not stored in DB */
	authorName?: string
	authorRole?: UserRole
}

export interface Attachment {
	id: string
	name: string
	type: string
	size: number
	url: string
	uploadedAt: Date
	uploadedBy: string
}

// === NEW TYPES FOR OPTIMIZED SYSTEM ===

export interface ReportVersion {
	id: string
	reportId: string
	versionNumber: number
	title: string
	content: TipTapContent
	templateId?: string // Template reference for this version
	parameters: ReportFormData
	generatedDocument?: GeneratedDocument
	chatConversationId?: string
	messages?: MessageData[] // Store chat messages in the version
	authorId: string
	organizationId: string
	status: ReportVersionStatus
	aiMetadata?: AIProcessingMetadata
	createdAt: Date
	updatedAt: Date
	sectionSnapshots?: SectionSnapshot[]
	contractScope?: ContractScope
	documentType?: DocumentType
	sectionMode?: SectionMode
}

/** Point-in-time snapshot of a single section, embedded in ReportVersion */
export interface SectionSnapshot {
	sectionId: string
	sectionDefinitionId: string
	order: number
	title: string
	content: TipTapContent
	status: string
	isEnabled: boolean
	completionPercentage: number
	version: number
	sectionType?: string
	generationPrompt?: string
	keyElements?: string[]
	source?: ReportSectionDocument["source"]
	isLocked?: boolean
	templateSectionId?: string
	sourceTemplateId?: string
	sourceSectionId?: string
	isExtra?: boolean
	matchedSectionId?: string | null
	chatHistory?: Array<{ role: string; content: string; timestamp: number; intent?: string }>
	exampleContent?: string
	writingStyle?: Record<string, unknown>
	contentPatterns?: Record<string, unknown>[]
	aiContext?: Record<string, unknown>
}

export interface GeneratedDocument {
	format: ExportFormat
	url: string
	size: number
	generatedAt: Date
	templateUsed?: string
}

export interface ChatConversation {
	id: string
	reportId: string
	reportVersionId?: string
	participantIds: string[]
	messages: ChatMessage[]
	status: ConversationStatus
	aiContext?: AIConversationContext
	organizationId: string
	createdAt: Date
	updatedAt: Date
}

export interface AIConversationContext {
	previousVersions: string[]
	extractedData: ExtractedData
	improvementSuggestions: ImprovementSuggestion[]
	confidence: number
}

export interface ImprovementSuggestion {
	field: string
	currentValue: string
	suggestedValue: string
	reasoning: string
	confidence: number
}

export interface Notification {
	id: string
	recipientId: string
	senderId: string
	type: NotificationType
	title: string
	message: string
	entityType: EntityType
	entityId: string
	organizationId: string
	metadata?: NotificationMetadata
	isRead: boolean
	readAt?: Date
	createdAt: Date
}

export interface NotificationMetadata {
	reportId?: string
	versionId?: string
	versionNumber?: number
	actionRequired?: boolean
	priority?: Priority
}

export interface ChangeLog {
	id: string
	entityType: EntityType
	entityId: string
	action: ChangeAction
	userId: string
	organizationId: string
	changes: Record<string, unknown>
	previousValues?: Record<string, unknown>
	timestamp: Date
}

export interface AIProcessingMetadata {
	provider: AIProvider
	model: string
	tokensUsed: number
	processingTime: number
	confidence: number
	iterations: number
	improvementsMade: string[]
}

export interface ResponsibilityTransfer {
	reportId: string
	fromRole: UserRole
	toRole: UserRole
	transferredBy: string
	transferredAt: Date
	comments?: string
}

// === ENHANCED ENUMS ===

export enum ReportVersionStatus {
	DRAFT = "draft",
	PROCESSING = "processing",
	READY = "ready",
	REVIEWED = "reviewed",
	APPROVED = "approved",
	REJECTED = "rejected",
}

export enum ConversationStatus {
	ACTIVE = "active",
	PAUSED = "paused",
	COMPLETED = "completed",
	ARCHIVED = "archived",
}

export enum NotificationType {
	NEW_VERSION_FOR_REVIEW = "new_version_for_review",
	VERSION_REVIEWED = "version_reviewed",
	VERSION_APPROVED = "version_approved",
	VERSION_REJECTED = "version_rejected",
	REPORT_CREATED = "report_created",
	REPORT_UPDATED = "report_updated",
	SYSTEM_NOTIFICATION = "system_notification",
}

export enum EntityType {
	REPORT = "report",
	REPORT_VERSION = "reportVersion",
	USER = "user",
	ORGANIZATION = "organization",
	CHAT_CONVERSATION = "chatConversation",
}

export enum ChangeAction {
	CREATE = "create",
	UPDATE = "update",
	DELETE = "delete",
	APPROVE = "approve",
	REJECT = "reject",
	ARCHIVE = "archive",
	TRANSFER_RESPONSIBILITY = "transfer_responsibility",
}

export interface FieldValidation {
	minLength?: number
	maxLength?: number
	pattern?: string
	customValidation?: string
}

// Enums
/**
 * Unified LCSP role model (2026-04-18).
 *
 * Legacy values (GERENCIA, CONTRACTACIO) kept for backward compat with
 * existing data rows (reports.currentResponsible). Users migrate to ADMIN.
 * New features should use the 10 unified roles below.
 */
export enum UserRole {
	// Legacy (deprecated — do not use in new code)
	GERENCIA = "gerencia",
	CONTRACTACIO = "contractacio",

	// Unified LCSP roles (11)
	TECNIC_CONTRACTACIO = "tecnic_contractacio",
	UNITAT_PROMOTORA = "unitat_promotora",
	CAP_CONTRACTACIO = "cap_contractacio",
	SECRETARI = "secretari",
	INTERVENTOR = "interventor",
	ORGAN_CONTRACTACIO = "organ_contractacio",
	RESPONSABLE_CONTRACTE = "responsable_contracte",
	COMITE_EXPERTS = "comite_experts",
	AUDITOR_EXTERN = "auditor_extern",
	ADMIN = "admin",
	SUPER_ADMIN = "super_admin",
}

/** Canonical list of the 11 active roles (excludes legacy) */
export const ACTIVE_USER_ROLES: UserRole[] = [
	UserRole.TECNIC_CONTRACTACIO,
	UserRole.UNITAT_PROMOTORA,
	UserRole.CAP_CONTRACTACIO,
	UserRole.SECRETARI,
	UserRole.INTERVENTOR,
	UserRole.ORGAN_CONTRACTACIO,
	UserRole.RESPONSABLE_CONTRACTE,
	UserRole.COMITE_EXPERTS,
	UserRole.AUDITOR_EXTERN,
	UserRole.ADMIN,
	UserRole.SUPER_ADMIN,
]

/** Platform-seat bootstrap role — no permissions until an admin assigns a real one */
export const PENDING_ROLE = "PENDING"

/** Default role pre-selected in user creation UI + auto-created SSO users */
export const DEFAULT_USER_ROLE: UserRole = UserRole.TECNIC_CONTRACTACIO

/** Roles with elevated tool-management permissions */
export const ADMIN_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.SUPER_ADMIN]

export enum OrganizationType {
	MUNICIPAL = "municipal",
	PROVINCIAL = "provincial",
	REGIONAL = "regional",
	NATIONAL = "national",
	OTHER = "other",
}

export enum ReportStatus {
	DRAFT = "draft",
	IN_REVIEW = "in_review",
	APPROVED = "approved",
	CHANGES_REQUESTED = "changes_requested",
	PUBLISHED = "published",
	ARCHIVED = "archived",
}

export enum ReviewStatus {
	PENDING = "pending",
	IN_PROGRESS = "in_progress",
	APPROVED = "approved",
	REJECTED = "rejected",
	CHANGES_REQUESTED = "changes_requested",
}

export enum ReportCategory {
	OBRES = "obres",
	SERVEIS = "serveis",
	SUBMINISTRAMENTS = "subministraments",
}

export enum Priority {
	LOW = "low",
	MEDIUM = "medium",
	HIGH = "high",
	URGENT = "urgent",
}

export enum ContractProcessType {
	AMB_CONCURRENCIA = "amb_concurrencia",
	SENSE_CONCURRENCIA = "sense_concurrencia",
}

export enum ContractScope {
	MINOR = "minor",
	NON_MINOR = "non_minor",
}

export enum DocumentType {
	INFORME_JUSTIFICATIU = "informe_justificatiu",
	PCAP = "pcap",
	PPT = "ppt",
}

export enum SectionMode {
	ASSISTANT = "assistant",
	TEMPLATE_GUIDED = "template_guided",
	TEMPLATE_DRIVEN = "template_driven",
}

export enum TemplateDocumentType {
	MINOR = "minor",
	PCAP = "pcap",
	PPT = "ppt",
	IJ = "ij",
}

export enum BundleContractScope {
	MINOR = "minor",
	LICITACIO = "licitacio",
	STANDALONE_IJ = "standalone_ij",
}

/** A template bundle groups templates by contract scope */
export interface TemplateBundle {
	id: string
	name: string
	description?: string
	contractScope: BundleContractScope
	category: ReportCategory
	organizationId: string
	isDefault: boolean
	createdAt: FlexibleTimestamp
	updatedAt: FlexibleTimestamp
	/** Populated when fetching with templates included */
	templates?: TemplateBundleMember[]
}

/** A template within a bundle, with its document type role */
export interface TemplateBundleMember {
	id: string
	name: string
	description: string
	documentType: TemplateDocumentType
	category: ReportCategory
	organizationId: string
	sections: import("./document").SectionContentSynthesis[]
	completionPercentage: number
	isDefault: boolean
	createdAt: FlexibleTimestamp
}


export enum TermPeriod {
	DIES = "dies",
	SETMANES = "setmanes",
	MESOS = "mesos",
}

export enum FieldType {
	TEXT = "text",
	TEXTAREA = "textarea",
	NUMBER = "number",
	DATE = "date",
	SELECT = "select",
	MULTISELECT = "multiselect",
	BOOLEAN = "boolean",
	EMAIL = "email",
	URL = "url",
	CURRENCY = "currency",
}

export enum MessageType {
	USER = "user",
	ASSISTANT = "assistant",
	SYSTEM = "system",
}

export enum AIProvider {
	OPENAI = "openai",
	ANTHROPIC = "anthropic",
	AZURE = "azure",
	LOCAL = "local",
}

export enum CommentStatus {
	ACTIVE = "active",
	RESOLVED = "resolved",
	DISMISSED = "dismissed",
}

export enum ReportNoteType {
	USER = "user",
	SYSTEM_RESPONSIBILITY_TRANSFER = "system_responsibility_transfer",
}

// IJ Section Summary (key concepts + page number)
export interface IJSectionSummary {
	keyConcepts: string[]
	pageNumber: number
}

// IJ Extracted Data (from uploaded IJ PDF)
export interface IJExtractedData {
	pdfStoragePath: string
	pdfFileName: string
	formFields: Partial<ReportFormData>
	sectionContent: Record<string, string> // sectionId → extracted text (bullets as text for backward compat)
	sectionSummaries?: Record<string, IJSectionSummary> // NEW: structured key concepts + page numbers
	extractedAt: string
}

// === BRAND CONFIGURATION ===
// Inspired by DOCX styles.xml but simplified: a single "style sheet" per org
// that controls the full visual identity of generated documents.

export type BrandPreset = "institutional" | "modern" | "formal" | "custom"
export type HeaderLayout = "logo-left" | "logo-center" | "logo-text" | "text-only"
export type CoverAccentStyle = "left-border" | "top-bar" | "full-width" | "none"
export type PageNumberPosition = "left" | "center" | "right"
export type DecorativeStripePosition = "left" | "top" | "right" | "bottom"

// ── Identity ──────────────────────────────────────────────────────────
// Brand can have its own logo (independent from Organization.logoUrl in Settings).
// If brand logoUrl is set, document generation uses it; otherwise falls back to org logo.

export interface BrandIdentity {
	logoUrl?: string | null         // Brand-specific logo URL (overrides org logo in documents)
	logoSource?: "upload" | "settings" | null // How the brand logo was set
	sealUrl?: string | null         // Institutional seal/crest URL (for cover pages)
}

// ── Colors ────────────────────────────────────────────────────────────

export interface BrandColors {
	primary: string      // Main brand color (cover accents, decorative elements, dividers, borders)
	secondary: string    // Secondary color (table header backgrounds, cover backgrounds)
	heading: string      // Heading text color
	text: string         // Body text color
}

// ── Typography ────────────────────────────────────────────────────────
// Explicit sizes and spacing per heading level (like DOCX styles.xml).
// Replaces the old coarse "density" preset with real pt values.

export interface BrandTypographySizes {
	h1SizePt: number          // Default: 14
	h2SizePt: number          // Default: 12.5
	h3SizePt: number          // Default: 12
	bodySizePt: number        // Default: 11
}

export interface BrandTypographySpacing {
	h1SpaceBeforePt: number   // Default: 16
	h1SpaceAfterPt: number    // Default: 10
	h2SpaceBeforePt: number   // Default: 14
	h2SpaceAfterPt: number    // Default: 8
	h3SpaceBeforePt: number   // Default: 8
	h3SpaceAfterPt: number    // Default: 4
	bodySpaceAfterPt: number  // Default: 8
	lineHeight: number        // Multiplier, default: 1.4
}

export interface BrandTypography {
	headingFont: string       // Font for headings (e.g., "Georgia", "Inter")
	bodyFont: string          // Font for body text
	sizes: BrandTypographySizes
	spacing: BrandTypographySpacing
}

// ── Header ────────────────────────────────────────────────────────────

export interface BrandHeader {
	enabled: boolean
	layout: HeaderLayout
	showDivider: boolean
	dividerColor?: string
	text?: string                // Optional text next to or instead of logo
	showDecorativeBars: boolean  // Colored bars below logo
}

// ── Footer ────────────────────────────────────────────────────────────

export interface BrandFooter {
	enabled: boolean
	showPageNumbers: boolean
	pageNumberPosition: PageNumberPosition
	text?: string          // Custom footer text (e.g., org name, expedient reference)
	showDivider: boolean
}

// ── Cover Page ────────────────────────────────────────────────────────

export interface BrandDecorativeStripe {
	enabled: boolean
	color: string                    // Usually brand primary
	position: DecorativeStripePosition
	widthMm: number                  // Stripe thickness in mm (default: 5)
}

export interface BrandCoverPage {
	enabled: boolean
	showLogo: boolean
	logoSize: "small" | "medium" | "large"
	showSeal: boolean                // Show institutional seal/escudo on cover
	showOrgName: boolean
	showDate: boolean
	showHeaderOnCover: boolean       // Show header on cover page (default: false)
	showFooterOnCover: boolean       // Show footer on cover page (default: false)
	accentStyle: CoverAccentStyle
	decorativeStripe: BrandDecorativeStripe
}

// ── Page / Margins ────────────────────────────────────────────────────

export interface BrandMargins {
	top: number     // mm
	bottom: number  // mm
	left: number    // mm
	right: number   // mm
}

// ── Table Style ───────────────────────────────────────────────────────

export interface BrandTableStyle {
	headerBgColor: string       // Background color for table header row
	headerTextColor: string     // Text color for table header row
	borderColor: string         // Table border color
	alternateRows: boolean      // Enable alternating row shading
	alternateRowColor: string   // Color for alternating rows
}

// ── Watermark ─────────────────────────────────────────────────────────

export interface BrandWatermark {
	enabled: boolean
	type: "text" | "image"
	text?: string               // Watermark text (e.g., "ESBORRANY", org name)
	opacity: number             // 0.0 to 1.0 (default: 0.1)
}

// ── Root Config ───────────────────────────────────────────────────────

export interface BrandConfig {
	preset: BrandPreset
	identity: BrandIdentity
	colors: BrandColors
	typography: BrandTypography
	header: BrandHeader
	footer: BrandFooter
	coverPage: BrandCoverPage
	margins: BrandMargins
	tableStyle: BrandTableStyle
	watermark: BrandWatermark
	updatedAt?: string
}

// View types
export type ViewType =
	| "dashboard"
	| "tender-assistant"
	| "tenders"
	| "search-reports"
	| "templates"
	| "new-template"
	| "section-library"
	| "public-tenders"
	| "public-tenders-organizations"
	| "procurement-templates"
	| "evaluations-new"
	| "users"
	| "teams"
	| "organization"
	| "role-labels"
	| "audit"
	| "settings"
	| "admin"
	| "brand"
	| `report-${string}`
	| `template-${string}`
	| `expedient-${string}`
	| `bundle-${string}`
	| `public-tender-${string}`
	| `procurement-template-${string}`
	| `procurement-template-doc-${string}`

// API Response types
export interface ApiResponse<T> {
	success: boolean
	data?: T
	error?: string
	message?: string
}

export interface PaginatedResponse<T> {
	items: T[]
	total: number
	page: number
	limit: number
	hasNext: boolean
	hasPrev: boolean
}

// Form types
export interface ReportFormData {
	id?: string // Report ID when editing an existing report
	title: string // Report title
	briefDescription: string // "Descripció abreujada" - 3 paraules identificatives
	description: string // "Descripció" - Objecte de l'informe justificatiu
	contractProcessType: ContractProcessType // Tipus de tramitació de l'informe justificatiu menor
	selectionCriteria: string // Criteris (free text)
	category: ReportCategory // "Tipologia d'informe justificatiu" - Obres/Serveis/Subministraments
	estimatedBudget?: number // "Pressupost estimat (sense IVA)"
	termValue?: number // Termini - numeric value
	termPeriod?: TermPeriod // Termini - period type (dies/setmanes/mesos)
	templateId?: string
	customFields: { [key: string]: unknown }
	contractScope?: ContractScope
	documentType?: DocumentType
	expedientId?: string
	cpvCodes?: string[]
	budgetBase?: number
	ivaPercentage?: number
	locations?: string[]
	maxExtensions?: number
	extensionPeriod?: string
}

export type ReportBasicInfoExtractedData = Partial<
	Pick<ReportFormData, "title" | "briefDescription" | "description" | "contractProcessType" | "selectionCriteria" | "category" | "estimatedBudget" | "termValue" | "termPeriod" | "cpvCodes" | "locations" | "maxExtensions" | "extensionPeriod">
>

export interface ExtractBasicInfoRequest {
	fileName: string
	parts: Part[]
}

export interface ExtractBasicInfoResponse {
	success: boolean
	extracted: ReportBasicInfoExtractedData
	updatedFields: Array<keyof ReportBasicInfoExtractedData>
}

// Search and filter types
export interface ReportFilters {
	status?: ReportStatus[]
	category?: ReportCategory[]
	priority?: Priority[]
	dateRange?: DateRange
	author?: string
	search?: string
}

export interface DateRange {
	from: Date
	to: Date
}

// Export utilities
export interface ExportOptions {
	format: ExportFormat
	includeMetadata: boolean
	includeComments: boolean
	template?: string
}

export enum ExportFormat {
	PDF = "pdf",
	DOCX = "docx",
	HTML = "html",
	JSON = "json",
}

// Communication Notes
export interface ReportNote {
	id: string
	reportId: string
	message: string
	authorId: string
	authorName: string
	authorRole: UserRole
	authorOrganizationId?: string
	createdAt: FlexibleTimestamp
	isEdited?: boolean
	editedAt?: FlexibleTimestamp
	readBy?: string[] // Array of user IDs who have read this note
	type?: ReportNoteType // Distinguish between user and system notes (defaults to USER for backward compatibility)
	// Additional fields for system notes (responsibility transfers)
	systemData?: {
		fromRole?: UserRole
		toRole?: UserRole
		transferredBy?: string
		action?: string // e.g., "approved", "changes_requested", "transferred"
	}
}

export interface CreateReportNoteData {
	reportId: string
	authorId: string
	authorName: string
	authorRole: UserRole
	organizationId: string
	message: string
	type?: ReportNoteType // Default to USER if not specified
	systemData?: {
		fromRole?: UserRole
		toRole?: UserRole
		transferredBy?: string
		action?: string
	}
}

// Helper interface specifically for creating responsibility transfer notes
export interface CreateTransferNoteData {
	reportId: string
	fromRole: UserRole
	toRole: UserRole
	transferredBy: string
	transferredByName: string
	transferredByRole: UserRole // Add the actual role of the user performing the transfer
	action: "approved" | "changes_requested" | "transferred"
	organizationId: string
}

// Utility functions for creating system messages
export const createTransferMessage = (fromRole: UserRole, toRole: UserRole, action: string, transferredByName: string): string => {
	const fromRoleDisplay = i18n.t(`auth.roles.${fromRole}`)
	const toRoleDisplay = i18n.t(`auth.roles.${toRole}`)

	const actionKeyMap: Record<string, string> = {
		approved: "approved",
		changes_requested: "changesRequested",
		transferred: "transferred",
	}
	const messageKey = actionKeyMap[action] || "default"

	return i18n.t(`auth.transferMessages.${messageKey}`, {
		fromRole: fromRoleDisplay,
		toRole: toRoleDisplay,
		userName: transferredByName,
	})
}

// Helper function to create a system transfer note from transfer data
export const createSystemTransferNote = (transferData: CreateTransferNoteData): CreateReportNoteData => {
	return {
		reportId: transferData.reportId,
		authorId: transferData.transferredBy, // Use the actual user ID who performed the transfer
		authorName: transferData.transferredByName, // Use the actual user name
		authorRole: transferData.transferredByRole, // Use the actual user role for permission compliance
		organizationId: transferData.organizationId,
		message: createTransferMessage(transferData.fromRole, transferData.toRole, transferData.action, transferData.transferredByName),
		type: ReportNoteType.SYSTEM_RESPONSIBILITY_TRANSFER,
		systemData: {
			fromRole: transferData.fromRole,
			toRole: transferData.toRole,
			transferredBy: transferData.transferredBy,
			action: transferData.action,
		},
	}
}

// Notification popup types
export interface ReportNotificationSummary {
	reportId: string
	reportTitle: string
	unreadCount: number
}
